import { useEffect, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useNavigate } from "react-router-dom";
import { API } from "../config";
import { Menu } from "../components/ui/menu";
import { useProfile } from "../context/ProfileContext";

export default function UploadPage() {
  const { profile, loading } = useProfile();

  const [file, setFile] = useState<File | null>(null);
  const [online, setOnline] = useState<Boolean>(true);
  const [showProgress, setShowProgress] = useState<Boolean | null>(true);
  const [percent, setPercent] = useState<Number | null>(null);
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  let rawToken = null;
  try {
    rawToken = localStorage.getItem("token");
  } catch (e) {
    console.error("Error reading token from localStorage:", e);
  }
  const token = rawToken;

  const listenerOnline = (event: any) => {
    setOnline(true)
  };

  const listenerOffline = (event: any) => {
    setOnline(false)
  };

  useEffect(() => {
    window.addEventListener("online", listenerOnline)
    window.addEventListener("offline", listenerOffline)

    return () => {
      window.removeEventListener('scroll', listenerOnline);
      window.addEventListener("offline", listenerOffline)
    }
  }, []);

  const handleUploadFileTo = (endpoint: string) => async () => {
    handleUpload(async (formData) => {
      return fetch(`${API.BASE_URL}/upload/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })
    });
  };

  const handleUploadCounters = async () => {
    handleUpload(async (formData) => {
      return fetch(`${API.BASE_URL}/upload/counters`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })
    });
  };

  const handleUploadMembers = async () => {
    handleUpload(async (formData) => {
      return fetch(`${API.BASE_URL}/upload/members`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })
    });
  };

  const handleUploadHistory = () => {
    handleUpload(async (formData) => {
      return fetch(`${API.BASE_URL}/upload/history`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })
    });
  };

  const handleUploadTariffs = () => {
    handleUpload(async (formData) => {
      return fetch(`${API.BASE_URL}/upload/tariffs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })
    });
  };

  const handleUpload = async (call: (formData: FormData) => Promise<Response>) => {
    if (!file) {
      setError("Будь ласка, виберіть файл Excel.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setPercent(50);
      setShowProgress(true);
      setMessage("Файл завантажується...");
      setError("");
      const res = await call(formData).finally(() => {
        setPercent(100);
        setShowProgress(false);
      });

      const data = await res.json();
      if (res.ok) {
        setMessage("Файл успішно завантажено.");
        setError("");
      } else {
        setMessage("");
        setError(data.error || "Завантаження не вдалося.");
      }
    } catch (err) {
      setMessage("");
      setError("Помилка мережі");
    }
  };

  if (loading) return <p className="text-center p-4">Завантажується...</p>;

  return (
    <Menu>
      <div className="flex flex-col h-screen grid grid-cols-1 md:grid-cols-3">
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <div
              className={`text-white text-center ${online ? "bg-lime-900" : "bg-red-700"}`}>{online ? "ONLINE" : "OFFLINE"}</div>
            {showProgress && (
              <>
                <h1 className="text-xl font-semibold mb-4">Завантаження:</h1>
                <div className="flex items-center">
                  <label className="w-full" htmlFor="file">
                    <meter className="w-full" value={`${percent}`} max="100">
                      <span className="ml-2" children={<>{percent}%</>}/>
                    </meter>
                  </label>
                </div>
              </>
            )}
            {message && <p className="text-green-600 mt-4 text-sm">{message}</p>}
            {error && <p className="text-red-500 mt-4 text-sm">{error}</p>}
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити members/removed Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("members/removed")}>Завантажити</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити members/info Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("members/info")}>Завантажити</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити members/area Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("members/area")}>Завантажити</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити members/residents Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("members/residents")}>Завантажити</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити membership Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("membership")}>Завантажити</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити counters Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("counters")}>Завантажити</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити history/bank Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("history/bank")}>Завантажит</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити history/cash Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("history/cash")}>Завантажити</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full max-w-lg mb-2 mt-2">
          <CardContent>
            <h1 className="text-xl font-semibold mb-4">Завантажити Тарифи Excel файл</h1>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <div className="flex justify-center items-center mt py-4">
              <Button onClick={handleUploadFileTo("tariffs")}>Завантажити Тарифи</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Menu>
  );
}
